import { useCallback, useEffect, useRef, useState } from 'react';

import { isEqual } from 'lodash';
import { useDebounce } from 'use-debounce';
import { useLazyQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import { pagination } from 'lane-shared/helpers/constants';
import { ClientTask } from 'graphql-query-contracts';

import { multiBuildingTasksQuery } from 'graphql-queries';

const FETCH_POLICY = 'network-only';
const DEBOUNCE_THROTTLE = 500;
const EMPTY_FILTER: MultiBuildingTaskFilterParams = {};

/**
 * Returns a referentially stable version of `value`.
 * Only updates the returned reference when the value deeply changes.
 */
function useDeepCompareMemoize<T>(value: T): T {
  const ref = useRef<T>(value);

  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }

  return ref.current;
}

export type MultiBuildingTaskFilterParams = {
  schedules?: string[];
  statuses?: string[];
  equipments?: string[];
  assignees?: string[];
  assigneeGroups?: string[];
  location?: string;
  dueDateStart?: Date;
  dueDateEnd?: Date;
  completedDateStart?: Date;
  completedDateEnd?: Date;
};

export type PageInfo = {
  total: number;
  start: number;
  perPage: number;
};

export type Task = ClientTask;

function buildFilter(
  searchParams: MultiBuildingTaskFilterParams
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(searchParams).filter(([, value]) => value !== undefined)
  );
}

export const useMultiBuildingTasks = (
  channelIds: string[],
  perPage: number = pagination.perPage,
  searchParams: MultiBuildingTaskFilterParams = EMPTY_FILTER
) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, DEBOUNCE_THROTTLE);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Stabilize reference types so that callers passing inline literals
  // do not cause infinite re-renders due to new references each render.
  const stableChannelIds = useDeepCompareMemoize(channelIds);
  const stableSearchParams = useDeepCompareMemoize(searchParams);

  const [fetchTasks, { data, loading, error, fetchMore, called }] =
    useLazyQuery(multiBuildingTasksQuery, {
      fetchPolicy: FETCH_POLICY,
      client: getClient(),
    });

  const buildVariables = useCallback(
    (startPage: number) => ({
      channelIds: stableChannelIds || [],
      pagination: {
        start: (startPage - 1) * perPage,
        perPage,
      },
      ...(debouncedSearch
        ? { search: { type: 'like', value: debouncedSearch } }
        : {}),
      filter: buildFilter(stableSearchParams),
    }),
    [perPage, stableChannelIds, debouncedSearch, stableSearchParams]
  );

  // Fetch page 2+ — appends results to existing list
  useEffect(() => {
    if (!called || page === 1) {
      return;
    }

    setIsFetchingMore(true);
    fetchMore({
      variables: buildVariables(page),
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const prevItems = prev.multiBuildingTasks.tasks;
        const newItems = fetchMoreResult.multiBuildingTasks.tasks;

        return {
          multiBuildingTasks: {
            __typename: 'SearchTasksResults',
            pageInfo: {
              ...fetchMoreResult.multiBuildingTasks.pageInfo,
              total: prev.multiBuildingTasks.pageInfo.total,
            },
            tasks: [...prevItems, ...newItems],
          },
        };
      },
    })
      .catch((err) => {
        console.error('useMultiBuildingTasks: fetchMore failed', err);
      })
      .finally(() => setIsFetchingMore(false));
  }, [page, buildVariables, called, fetchMore]);

  // Reset to page 1 and fetch fresh when channels/search/filters change
  useEffect(() => {
    setPage(1);
    fetchTasks({ variables: buildVariables(1) });
  }, [buildVariables, fetchTasks]);

  const refetch = () => {
    if (!loading) {
      setPage(1);
      fetchTasks({ variables: buildVariables(1) });
    }
  };

  const items: Task[] = data?.multiBuildingTasks?.tasks || [];
  const pageInfo: PageInfo = data?.multiBuildingTasks?.pageInfo || {
    total: 0,
    start: 0,
    perPage: 0,
  };

  const isInitialLoading = page === 1 && loading && items.length === 0;

  return {
    items,
    pageInfo,
    loading,
    isInitialLoading,
    isFetchingMore,
    error,
    page,
    setPage,
    search,
    setSearch,
    refetch,
  };
};
